import FastImage from '@d11/react-native-fast-image';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Dimensions, PixelRatio, Platform } from 'react-native';
import { clearAllStoredCredentials } from '../services/SecureCredentials';
import ChatwootService from '../services/ChatwootService';

export { normalizeEmail } from './normalizeEmail';

const dumpTokens = async () => {
  try {
    await AsyncStorage.removeItem('refreshToken');
    await AsyncStorage.removeItem('accessToken');
    await AsyncStorage.removeItem('userId');
    // Back Office owner session (same keys as useBackofficeOwnerAuth / useBackofficeRefresh)
    await AsyncStorage.removeItem('owner_access_token');
    await AsyncStorage.removeItem('owner_refresh_token');
    // Also clear any stored credentials used for Back Office auto-login
    await clearAllStoredCredentials();
    await ChatwootService.reset();
    return true;
  } catch {
    return false;
  }
};

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

// Based on iPhone 6/7/8 dimensions
const baseWidth = 375;
const baseHeight = 667;

const scaleWidth = SCREEN_WIDTH / baseWidth;
const scaleHeight = SCREEN_HEIGHT / baseHeight;

const BASE = { width: 375, height: 812 }; // tweak to your design baseline

type NormalizeOpts = {
  /** 0..1. If set, blends between raw size and scaled size. e.g. 0.5 = moderate */
  moderateFactor?: number;
  /** Clamp extremes (useful for tablets/tiny phones) */
  clamp?: { min?: number; max?: number };
  /** Use 'text' for font sizes so we can optionally respect accessibility scaling */
  type?: 'size' | 'text';
  /** For text: true (default) respects system font size; false normalizes it */
  respectA11y?: boolean;
  /**
   * How to derive the scale factor.
   * - 'based' (default): uses `based` param ('width' -> scaleW, 'height' -> scaleH)
   * - 'min': uses Math.min(scaleW, scaleH) (aspect-ratio safe)
   * - 'max': uses Math.max(scaleW, scaleH)
   * - 'width' / 'height': explicit
   * - 'none': disables scaling (useful for borders/hairlines)
   */
  scaleMode?: 'based' | 'min' | 'max' | 'width' | 'height' | 'none';
  /**
   * Aspect-ratio safe scaling for width-based calls.
   * Defaults to true for non-text when using width-based scaling, to avoid over-scaling on extreme ratios/tablets.
   */
  safeAspect?: boolean;
  /** Tablet heuristics (short-side breakpoint). */
  tablet?: { minShortSide?: number; factor?: number; enabled?: boolean };
};

const normalizeSize = (
  size: number,
  based: 'width' | 'height' = 'width',
  opts: NormalizeOpts = {},
) => {
  try {
    const { width, height, fontScale } = Dimensions.get('window');
    const shortSide = Math.min(width, height);
    const isTablet = shortSide >= (opts.tablet?.minShortSide ?? 600);

    const scaleW = width / BASE.width;
    const scaleH = height / BASE.height;

    // Border-like values should not scale by default (keeps hairlines sane).
    // This is intentionally conservative: only triggers for <= 1.
    if ((opts.scaleMode === 'none' || size <= 1) && opts.type !== 'text') {
      // On web, fractional borders can look broken; keep whole pixels.
      if (Platform.OS === 'web') return Math.max(1, size);
      // Default "1" becomes 0.5 on high-density screens.
      if (size === 1) return PixelRatio.get() >= 2 ? 0.5 : 1;
      return size;
    }

    const safeAspectDefault = opts.type !== 'text' && based === 'width';
    const safeAspect = opts.safeAspect ?? safeAspectDefault;

    let scale: number;
    switch (opts.scaleMode ?? 'based') {
      case 'none':
        scale = 1;
        break;
      case 'min':
        scale = Math.min(scaleW, scaleH);
        break;
      case 'max':
        scale = Math.max(scaleW, scaleH);
        break;
      case 'width':
        scale = scaleW;
        break;
      case 'height':
        scale = scaleH;
        break;
      case 'based':
      default:
        scale = based === 'height' ? scaleH : scaleW;
        break;
    }

    // Aspect-ratio safe fallback for width-based scaling (only if still width-ish).
    if (
      safeAspect &&
      (opts.scaleMode == null ||
        opts.scaleMode === 'based' ||
        opts.scaleMode === 'width') &&
      based === 'width'
    ) {
      scale = Math.min(scaleW, scaleH);
    }

    // raw scaled value
    let val = size * scale;

    // tablet damping (non-text by default)
    const tabletEnabled = opts.tablet?.enabled ?? true;
    const tabletFactor = opts.tablet?.factor ?? 0.9;
    if (tabletEnabled && isTablet && opts.type !== 'text') {
      val = val * tabletFactor;
    }

    // optional moderate scaling
    if (opts.moderateFactor != null) {
      val = size + (val - size) * opts.moderateFactor; // e.g. 0.5 softens changes
    }

    // text handling: respect or ignore accessibility fontScale
    const isText = opts.type === 'text';
    const respect = opts.respectA11y ?? true;
    if (isText && !respect && fontScale) {
      val = val / fontScale;
    }

    // round to device pixels (no extra Math.round)
    val = PixelRatio.roundToNearestPixel(val);

    // optional clamp
    if (opts.clamp) {
      const min = opts.clamp.min ?? -Infinity;
      const max = opts.clamp.max ?? Infinity;
      if (val < min) val = min;
      if (val > max) val = max;
    }

    return val;
  } catch (error) {
    console.log('Error in normalizeSize', error);
    return size;
  }
};

// (nice-to-have) tiny helpers if you like:
export const ns = (n: number, o?: NormalizeOpts) =>
  normalizeSize(n, 'width', o);
export const nvs = (n: number, o?: NormalizeOpts) =>
  normalizeSize(n, 'height', o);

/**
 * Semantic wrappers — prefer these over calling `normalizeSize` directly.
 * `normalizeSize` is internal to this module (not exported).
 */
export const fs = (n: number, o: Omit<NormalizeOpts, 'type'> = {}) =>
  normalizeSize(n, 'width', {
    type: 'text',
    // text generally wants width-based scaling (predictable) + clamp
    scaleMode: o.scaleMode ?? 'width',
    safeAspect: o.safeAspect ?? false,
    clamp: o.clamp ?? { min: n * 0.9, max: n * 1.25 },
    ...o,
  });

export const s = (n: number, o: NormalizeOpts = {}) =>
  normalizeSize(n, 'width', {
    scaleMode: o.scaleMode ?? 'min',
    moderateFactor: o.moderateFactor ?? 0.5,
    clamp: o.clamp ?? { min: n * 0.85, max: n * 1.3 },
    ...o,
  });

export const v = (n: number, o: NormalizeOpts = {}) =>
  normalizeSize(n, 'height', {
    // still aspect-safe on extreme ratios
    scaleMode: o.scaleMode ?? 'min',
    moderateFactor: o.moderateFactor ?? 0.4,
    clamp: o.clamp ?? { min: n * 0.85, max: n * 1.25 },
    ...o,
  });

export const iconSize = (n: number, o: NormalizeOpts = {}) =>
  normalizeSize(n, 'width', {
    scaleMode: o.scaleMode ?? 'min',
    moderateFactor: o.moderateFactor ?? 0.3,
    clamp: o.clamp ?? { min: n, max: n * 1.4 },
    ...o,
  });

/** Borders should not scale (by design). */
export const borderWidth = (n: number = 1) => {
  if (n <= 0) return 0;
  if (Platform.OS === 'web') return Math.max(1, n);
  if (n === 1) return PixelRatio.get() >= 2 ? 0.5 : 1;
  return n;
};
/** App-wide max fractional precision for qty/mass/price/money helpers. */
export const NUMERIC_DECIMAL_PLACES = 3;

/** Integers → 0dp; non-integers → up to NUMERIC_DECIMAL_PLACES. */
export function getNumericDisplayDecimals(n: number): number {
  return Number.isInteger(n) ? 0 : NUMERIC_DECIMAL_PLACES;
}

/** Round a number to NUMERIC_DECIMAL_PLACES. */
export function roundToNumericPrecision(n: number): number {
  const factor = Math.pow(10, NUMERIC_DECIMAL_PLACES);
  return Math.round((Number(n) + Number.EPSILON) * factor) / factor;
}

/** True when value rounds to 0 at app numeric precision (avoids float residue). */
export function isNumericEffectivelyZero(n: number): boolean {
  return roundToNumericPrecision(n) === 0;
}

// ---------------------------------------------------------------------------
// Compact Y-axis tick formatting (K / M / B)
// ---------------------------------------------------------------------------

type AxisUnit = 'none' | 'K' | 'M' | 'B';

/**
 * Pick a single compact unit for the entire Y-axis based on the max absolute
 * value.  Every tick on the axis should use the same unit so they never flip
 * between e.g. "800" and "1.2K".
 */
function axisUnit(maxAbs: number): AxisUnit {
  if (maxAbs >= 1e9) return 'B';
  if (maxAbs >= 1e6) return 'M';
  if (maxAbs >= 1e3) return 'K';
  return 'none';
}

/**
 * Format a single Y-axis tick value using the pre-computed axis unit.
 *
 * Rules:
 *  - 0 always renders as "0" (never "0K").
 *  - ≥ 100 before the suffix: integer only (e.g. "821K") — max 4 chars.
 *  - < 100: up to 1 decimal place (e.g. "64.5K") — max 5 chars.
 *  - Trailing ".0" is dropped.
 *  - Values below the unit threshold get comma-separated (via `formatWithCommas`).
 */
function compactAxisTick(n: number, unit: AxisUnit): string {
  if (n === 0) return '0';
  const divisors: Record<AxisUnit, number> = { none: 1, K: 1e3, M: 1e6, B: 1e9 };
  const scaled = n / divisors[unit];
  if (unit === 'none') {
    return formatWithCommas(Math.round(n), 0);
  }
  const formatted = Number.isInteger(scaled) ? String(scaled) : scaled.toFixed(1).replace(/\.0$/, '');
  return `${formatted}${unit}`;
}

function formatWithCommas(
  n: number,
  decimals: number = 0,
  truncate: boolean = false,
): string {
  if (n == null || Number.isNaN(n)) {
    return '0';
  }

  const decimalPlaces = Math.max(0, decimals);
  const factor = Math.pow(10, decimalPlaces);

  const value = truncate ? Math.trunc(n * factor) / factor : n;

  let formatted = value.toFixed(decimalPlaces);

  // Remove trailing zeros AND dangling decimal point
  formatted = formatted.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');

  return formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Format a numeric string for display with comma separators
 * @param value - Numeric string (e.g., "1000" or "1000.50")
 * @returns Formatted string with commas (e.g., "1,000" or "1,000.50")
 */
export function formatNumericStringWithCommas(value: string): string {
  if (!value || value === '') return '';

  // Remove any existing commas
  const cleaned = value.replace(/,/g, '');
  const hasTrailingDecimal =
    cleaned.endsWith('.') && cleaned.indexOf('.') === cleaned.lastIndexOf('.');

  // Split by decimal point
  const parts = cleaned.split('.');
  const integerPart = parts[0] || '';
  const decimalPart = parts[1] || '';

  // Add commas to integer part
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // Combine with decimal part if exists
  if (hasTrailingDecimal) return `${formattedInteger}.`;
  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
}

/**
 * Parse a formatted numeric string (with commas) back to a clean numeric string
 * @param value - Formatted string with commas (e.g., "1,000" or "1,000.50")
 * @returns Clean numeric string without commas (e.g., "1000" or "1000.50")
 */
export function parseFormattedNumericString(value: string): string {
  if (!value || value === '') return '';

  // Remove commas and keep only digits and decimal point
  // Remove leading zeros except for zero itself
  return value
    .replace(/,/g, '')
    .replace(/[^0-9.]/g, '')
    .replace(/^0+(?=\d)/, '');
}

/**
 * Sanitize numeric input based on product type.
 * - Item type: integers only (no decimals).
 * - Unit type: allow up to maxDecimals (default NUMERIC_DECIMAL_PLACES).
 *   Preserves a trailing "." while typing and normalizes leading "." to "0.".
 */
export function sanitizeNumericInput(
  value: string,
  options: { isUnit: boolean; maxDecimals?: number },
): string {
  const { isUnit, maxDecimals = NUMERIC_DECIMAL_PLACES } = options;
  if (!value || value === '') return '';

  // Remove leading zeros except for zero itself
  let cleaned = value
    .replace(/,/g, '')
    .replace(/[^0-9.]/g, '')
    .replace(/^0+(?=\d)/, '');
  if (!cleaned) return '';

  if (!isUnit) {
    return cleaned.replace(/\./g, '');
  }

  if (cleaned.startsWith('.')) cleaned = `0${cleaned}`;

  const decimalIndex = cleaned.indexOf('.');
  if (decimalIndex === -1) return cleaned;

  const integerPart = cleaned.slice(0, decimalIndex);
  const decimalPart = cleaned
    .slice(decimalIndex + 1)
    .replace(/\./g, '')
    .slice(0, maxDecimals);

  // Keep trailing "." so users can continue typing fractional digits.
  return `${integerPart}.${decimalPart}`;
}

/**
 * Format a table cell value:
 * - If numeric (number or numeric string, including scientific notation),
 *   render integers with no decimals and decimals with up to NUMERIC_DECIMAL_PLACES.
 * - Preserve thousands separators.
 * - Collapse tiny floating point noise (|n| < 1e-8) to 0 to avoid 2.84e-14.
 * - Leave non-numeric strings (e.g. "KES 1,000", "3 kg") unchanged.
 */
export function formatTableCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') {
    let n = Math.abs(value) < 1e-8 ? 0 : value;
    return formatWithCommas(Number(n), getNumericDisplayDecimals(n));
  }

  if (typeof value !== 'string') {
    return '';
  }

  const str = value.trim();
  // Quickly ignore obvious non-numeric cells (contains letters except scientific notation 'e')
  // Allow commas and spaces which we'll strip before parsing
  const cleaned = str.replace(/,/g, '');
  const numericPattern = /^-?(?:\d+\.?\d*|\d*\.?\d+)(?:e[-+]?\d+)?$/i;
  if (!numericPattern.test(cleaned)) return str;

  let n = Number(cleaned);
  if (!Number.isFinite(n)) return str;
  n = Math.abs(n) < 1e-8 ? 0 : n;
  return formatWithCommas(Number(n), getNumericDisplayDecimals(n));
}
function formatNumberDecimal(num: number): string {
  const n = Number(num);
  if (!Number.isFinite(n)) return '0';
  return formatWithCommas(n, getNumericDisplayDecimals(n), true);
}
const forceHttps = (url: string | undefined | null): string => {
  if (!url || typeof url !== 'string') return '';

  if (
    url.startsWith('http') ||
    url.startsWith('file://') ||
    url.startsWith('content://') ||
    url.startsWith('data:')
  )
    return url.replace(/^http:\/\//i, 'https://');
  // naive base64 check and prefix for preview
  if (url.length > 100) {
    return `data:image/jpeg;base64,${url}`;
  }
  return url.replace(/^http:\/\//i, 'https://');
};

function capitalizeFirstLetter(val: string) {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}

// Battle-tested Safaricom number validation (local format only: 10 digits)
export const safaricomNumberValidation = (value: string) => {
  if (value.trim().length > 10) {
    return false;
  }

  if (value.trim().length === 10) {
    if (value.trim().startsWith('070')) {
      return true;
    } else if (value.trim().startsWith('071')) {
      return true;
    } else if (value.trim().startsWith('072')) {
      return true;
    } else if (value.trim().startsWith('074')) {
      return true;
    } else if (value.trim().startsWith('0757')) {
      return true;
    } else if (value.trim().startsWith('0758')) {
      return true;
    } else if (value.trim().startsWith('0759')) {
      return true;
    } else if (value.trim().startsWith('0768')) {
      return true;
    } else if (value.trim().startsWith('0769')) {
      return true;
    } else if (value.trim().startsWith('079')) {
      return true;
    } else if (value.trim().startsWith('011')) {
      return true;
    } else {
      return false;
    }
  }

  return false;
};

export const normalizeSafaricomPhone = (raw: string) => {
  if (!raw) return '';
  let phone = raw.replace(/[^\d+]/g, '').trim();
  if (phone.startsWith('+')) {
    phone = phone.substring(1);
  }
  if (phone.startsWith('2547') && phone.length === 12) {
    return '0' + phone.substring(3);
  }
  if (phone.startsWith('2541') && phone.length === 12) {
    return '0' + phone.substring(3);
  }
  if (phone.startsWith('07') && phone.length === 10) {
    return phone;
  }
  if (phone.startsWith('01') && phone.length === 10) {
    return phone;
  }
  return phone;
};

/**
 * Currency display format configuration
 * Maps ISO currency codes to their display formats
 * Format: { code: 'displayFormat' }
 * Example: KES -> 'Ksh' (Kenyan Shilling)
 */
const CURRENCY_DISPLAY_FORMATS: Record<string, string> = {
  // Kenya
  KES: 'Ksh',
  USD: '$',
  EUR: '€',
  GBP: '£',
  UGX: 'USh',
  TZS: 'TSh',
  RWF: 'RF',
};

/**
 * Converts a currency code to its display format
 * @param currencyCode - The ISO currency code (e.g., "KES")
 * @returns The display format (e.g., "Ksh") or the code itself if not found
 */
function getCurrencyDisplayFormat(currencyCode: string): string {
  if (!currencyCode) return '';

  const upperCode = currencyCode.toUpperCase();
  return CURRENCY_DISPLAY_FORMATS[upperCode] || upperCode;
}

/**
 * Replaces KSH/kes/Ksh/ksh currency strings with the location currency
 * while maintaining the original capitalization pattern.
 * Uses currency display format mapping for proper conversion.
 * @param text - The text string that may contain currency references
 * @param currency - The currency code in capital letters (e.g., "KES")
 * @returns The text with currency strings replaced while maintaining capitalization
 */
function replaceCurrencyString(text: string, currency: string): string {
  if (!text || !currency) return text;

  // Get the display format for the currency (e.g., KES -> Ksh)
  const displayFormat = getCurrencyDisplayFormat(currency);
  const currencyCode = currency.toUpperCase();

  // Get different capitalization variants
  const displayFormatLower = displayFormat.toLowerCase();
  const displayFormatCapitalized =
    displayFormat.charAt(0).toUpperCase() +
    displayFormat.slice(1).toLowerCase();
  const displayFormatUpper = displayFormat.toUpperCase();

  // Replace while maintaining capitalization patterns
  // Handle both old format (ksh/kes) and new display format
  return text
    .replace(/\bksh\b/g, displayFormatLower) // lowercase: ksh -> ksh (or currency display lower)
    .replace(/\bKsh\b/g, displayFormatCapitalized) // Capital first: Ksh -> Ksh (or currency display capitalized)
    .replace(/\bKSH\b/g, displayFormatUpper) // all caps: KSH -> KSH (or currency display upper)
    .replace(/\bkes\b/g, displayFormatLower) // lowercase: kes -> ksh (or currency display lower)
    .replace(/\bKes\b/g, displayFormatCapitalized) // Capital first: Kes -> Ksh (or currency display capitalized)
    .replace(/\bKES\b/g, currencyCode); // all caps: KES -> KES (keep code format)
}

async function clearFastImageCache() {
  try {
    await Promise.all([
      FastImage.clearDiskCache(),
      FastImage.clearMemoryCache(),
    ]);
  } catch (error) {
    console.log('Error in clearFastImageCache', error);
  }
}

export {
  dumpTokens,
  scaleWidth,
  scaleHeight,
  formatWithCommas,
  formatNumberDecimal,
  axisUnit,
  compactAxisTick,
  forceHttps,
  capitalizeFirstLetter,
  clearFastImageCache,
  replaceCurrencyString,
  getCurrencyDisplayFormat,
};
